import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/I18nContext";
import translations from "../i18n/translations";
import StudentShell from "../components/StudentShell/StudentShell";
import * as classesService from "../services/classes";
import * as homeworksService from "../services/homeworks";
import * as studentsService from "../services/students";
import { dueBadge, formatDateTime } from "../utils/homeworkDates";
import "./Homeworks.css";

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function roundScore(value) {
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n * 10) / 10 : null;
}

const EMPTY_CREATE = { title: "", due_at: "", target: "class", targetId: "" };

export default function Homeworks() {
  const navigate = useNavigate();
  const { user, accessToken, effectiveRole, activeStudentId } = useAuth();
  const { t, language } = useTranslation();
  const dir = translations[language]?.meta?.dir || "ltr";
  const tr = useCallback((key, options) => t(`homeworkUi.${key}`, options), [t]);

  const isTeacherOrAdmin = ["teacher", "admin", "superadmin"].includes(effectiveRole);
  const isStudent = effectiveRole === "student";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [homeworks, setHomeworks] = useState([]);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [filter, setFilter] = useState("all");

  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE);
  const [createBusy, setCreateBusy] = useState(false);

  useEffect(() => {
    let ignore = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const params = {};
        if (user?.role === "parent") {
          if (!activeStudentId) {
            throw new Error(
              t("homeworks.parent.pickChild", {
                defaultValue: "Pick a child first from the dashboard.",
              }),
            );
          }
          params.student_id = activeStudentId;
        }
        const isAdmin = ["admin", "superadmin"].includes(effectiveRole);
        const [hwRes, classesRes, studentsRes] = await Promise.all([
          homeworksService.fetchHomeworks({ accessToken, params }),
          isTeacherOrAdmin
            ? classesService
                .fetchClasses({ accessToken, params: { page: 1 } })
                .catch(() => ({ classes: [] }))
            : { classes: [] },
          isAdmin
            ? studentsService.fetchStudents({ accessToken }).catch(() => ({ students: [] }))
            : { students: [] },
        ]);
        if (ignore) return;
        setHomeworks(asArray(hwRes?.homeworks));
        setClasses(asArray(classesRes?.classes));
        setStudents(asArray(studentsRes?.students));
      } catch (e) {
        if (!ignore) {
          setHomeworks([]);
          setError(e?.message || "");
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    if (accessToken) load();
    else setLoading(false);
    return () => {
      ignore = true;
    };
  }, [accessToken, activeStudentId, effectiveRole, isTeacherOrAdmin, t, user?.role]);

  // Every student the teacher can assign to (from classes, plus all for admins).
  const allStudents = useMemo(() => {
    const seen = new Map();
    classes.forEach((g) =>
      asArray(g.students).forEach((s) => s?.id && seen.set(Number(s.id), s)),
    );
    students.forEach((s) => s?.id && seen.set(Number(s.id), s));
    return [...seen.values()].sort((a, b) =>
      String(a.full_name || "").localeCompare(String(b.full_name || "")),
    );
  }, [classes, students]);

  const targetLabel = (hw) => {
    const classId = asArray(hw.assigned_class_group_ids)[0];
    if (classId) {
      const name = classes.find((g) => Number(g.id) === Number(classId))?.name;
      return tr("forClass", { name: name || `#${classId}` });
    }
    const studentId = asArray(hw.assigned_student_ids)[0];
    if (studentId) {
      const name = allStudents.find((s) => Number(s.id) === Number(studentId))?.full_name;
      return tr("forStudent", { name: name || `#${studentId}` });
    }
    return tr("notAssigned");
  };

  const doCreate = async (e) => {
    e.preventDefault();
    if (!accessToken || !createForm.title.trim()) return;
    setCreateBusy(true);
    setError("");
    try {
      const id = createForm.targetId ? [Number(createForm.targetId)] : [];
      const created = await homeworksService.createHomework({
        accessToken,
        payload: {
          title: createForm.title.trim(),
          description: "",
          due_at: createForm.due_at || null,
          status: "draft",
          assigned_class_group_ids: createForm.target === "class" ? id : [],
          assigned_student_ids: createForm.target === "student" ? id : [],
        },
      });
      navigate(`/homeworks/${created.id}/edit`);
    } catch (e2) {
      setError(e2?.message || "");
    } finally {
      setCreateBusy(false);
    }
  };

  const doDelete = async (hw) => {
    if (!window.confirm(tr("confirmDelete"))) return;
    setError("");
    try {
      await homeworksService.deleteHomework({ accessToken, homeworkId: hw.id });
      setHomeworks((prev) => prev.filter((x) => x.id !== hw.id));
    } catch (e) {
      setError(e?.message || "");
    }
  };

  const doTogglePublish = async (hw) => {
    setError("");
    try {
      const updated = await homeworksService.updateHomework({
        accessToken,
        homeworkId: hw.id,
        payload: { status: hw.status === "published" ? "draft" : "published" },
      });
      setHomeworks((prev) =>
        prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)),
      );
    } catch (e) {
      setError(e?.message || "");
    }
  };

  const teacherList = useMemo(() => {
    if (!isTeacherOrAdmin) return [];
    return homeworks.filter((hw) => {
      if (filter === "toReview") return (hw.stats?.to_review || 0) > 0;
      if (filter === "draft") return hw.status !== "published";
      if (filter === "published") return hw.status === "published";
      return true;
    });
  }, [filter, homeworks, isTeacherOrAdmin]);

  const toReviewTotal = homeworks.reduce((n, hw) => n + (hw.stats?.to_review || 0), 0);

  // Student: to do (soonest first), sent, corrected.
  const studentGroups = useMemo(() => {
    if (isTeacherOrAdmin) return null;
    const dueTs = (hw) => {
      const ts = hw.due_at ? new Date(hw.due_at).getTime() : NaN;
      return Number.isFinite(ts) ? ts : Infinity;
    };
    const todo = homeworks.filter((hw) => !hw.submission).sort((a, b) => dueTs(a) - dueTs(b));
    const sent = homeworks.filter((hw) => hw.submission && hw.submission.status !== "reviewed");
    const corrected = homeworks.filter((hw) => hw.submission?.status === "reviewed");
    return { todo, sent, corrected };
  }, [homeworks, isTeacherOrAdmin]);

  const renderTeacherCard = (hw) => {
    const stats = hw.stats || {};
    const badge = hw.due_at && hw.status === "published" ? dueBadge(hw.due_at, tr) : null;
    const pct = stats.assigned ? Math.round((stats.submitted / stats.assigned) * 100) : 0;
    return (
      <article className="hw-card" key={hw.id}>
        <div className="hw-card__top">
          <span className={`hw-chip hw-chip--${hw.status}`}>
            {tr(`status.${hw.status}`, { defaultValue: hw.status })}
          </span>
          {stats.to_review ? (
            <span className="hw-chip hw-chip--review">
              {tr("toReview", { count: stats.to_review })}
            </span>
          ) : null}
        </div>
        <Link to={`/homeworks/${hw.id}/edit`} className="hw-card__title">
          {hw.title || `#${hw.id}`}
        </Link>
        <p className="hw-card__line">
          <i className="fa-solid fa-users" aria-hidden="true" /> {targetLabel(hw)}
        </p>
        <p className="hw-card__line">
          <i className="fa-regular fa-calendar" aria-hidden="true" />{" "}
          {hw.due_at ? tr("due", { date: formatDateTime(hw.due_at, language) }) : tr("noDue")}
          {badge ? <span className={`hw-due hw-due--${badge.tone}`}>{badge.text}</span> : null}
        </p>

        {hw.status === "published" && stats.assigned ? (
          <div className="hw-card__progress">
            <div className="hw-card__bar">
              <span style={{ width: `${pct}%` }} />
            </div>
            <small>{tr("handedIn", { done: stats.submitted, total: stats.assigned })}</small>
          </div>
        ) : null}

        <div className="hw-card__actions">
          <Link className="btn primary" to={`/homeworks/${hw.id}/edit`}>
            {tr("edit")}
          </Link>
          <Link className="btn ghost" to={`/homeworks/${hw.id}/submissions`}>
            {tr("copies")}
            {stats.submitted ? <span className="hw-card__count">{stats.submitted}</span> : null}
          </Link>
          <span className="hw-card__icons">
            <Link
              className="hw-icon"
              to={`/homeworks/${hw.id}`}
              title={tr("preview")}
              aria-label={tr("preview")}
            >
              <i className="fa-regular fa-eye" aria-hidden="true" />
            </Link>
            <button
              type="button"
              className="hw-icon"
              onClick={() => doTogglePublish(hw)}
              title={hw.status === "published" ? tr("unpublish") : tr("publish")}
              aria-label={hw.status === "published" ? tr("unpublish") : tr("publish")}
            >
              <i
                className={`fa-solid ${hw.status === "published" ? "fa-eye-slash" : "fa-paper-plane"}`}
                aria-hidden="true"
              />
            </button>
            <button
              type="button"
              className="hw-icon hw-icon--danger"
              onClick={() => doDelete(hw)}
              title={tr("delete")}
              aria-label={tr("delete")}
            >
              <i className="fa-solid fa-trash" aria-hidden="true" />
            </button>
          </span>
        </div>
      </article>
    );
  };

  const renderStudentItem = (hw) => {
    const sub = hw.submission;
    const badge = !sub && hw.due_at ? dueBadge(hw.due_at, tr) : null;
    const score = roundScore(sub?.score);
    const max = roundScore(sub?.max_score);
    return (
      <li className={`hw-item${!sub ? " is-todo" : ""}`} key={hw.id}>
        <span className={`hw-item__icon${sub ? " is-done" : ""}`} aria-hidden="true">
          <i className={sub ? "fa-solid fa-check" : "fa-solid fa-pen"} />
        </span>
        <span className="hw-item__text">
          <strong>{hw.title || `#${hw.id}`}</strong>
          <small>
            {hw.due_at ? tr("due", { date: formatDateTime(hw.due_at, language) }) : tr("noDue")}
          </small>
          {badge ? <span className={`hw-due hw-due--${badge.tone}`}>{badge.text}</span> : null}
          {sub && sub.status !== "reviewed" ? (
            <span className="hw-due hw-due--sent">{tr("student.waiting")}</span>
          ) : null}
        </span>
        {sub?.status === "reviewed" && score !== null && max ? (
          <span className="hw-item__score">{tr("student.score", { score, max })}</span>
        ) : null}
        <Link className={`btn ${sub ? "ghost" : "primary"}`} to={`/homeworks/${hw.id}`}>
          {sub ? tr("student.view") : tr("student.start")}
        </Link>
      </li>
    );
  };

  const content = (
    <main className="homeworks-page" dir={dir}>
      <div className="container homeworks-page__container">
        <header className="homeworks-page__header">
          <div>
            <h1 className="homeworks-page__title">{tr("title")}</h1>
            <p className="homeworks-page__subtitle">
              {isTeacherOrAdmin ? tr("teacherSubtitle") : tr("studentSubtitle")}
            </p>
          </div>
          {isTeacherOrAdmin && !creating ? (
            <button type="button" className="btn primary" onClick={() => setCreating(true)}>
              <i className="fa-solid fa-plus" aria-hidden="true" /> {tr("newHomework")}
            </button>
          ) : null}
        </header>

        {error ? <div className="homeworks-page__error">{error}</div> : null}

        {isTeacherOrAdmin && creating ? (
          <form className="hw-create" onSubmit={doCreate}>
            <h2>{tr("create.title")}</h2>
            <label className="hw-create__full">
              <span>{tr("create.name")}</span>
              <input
                autoFocus
                required
                value={createForm.title}
                placeholder={tr("create.namePlaceholder")}
                onChange={(e) => setCreateForm((p) => ({ ...p, title: e.target.value }))}
              />
            </label>
            <div className="hw-create__full">
              <span className="hw-create__label">{tr("create.for")}</span>
              <div className="hw-segment" role="radiogroup">
                {["class", "student"].map((value) => (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={createForm.target === value}
                    className={createForm.target === value ? "is-active" : ""}
                    onClick={() => setCreateForm((p) => ({ ...p, target: value, targetId: "" }))}
                  >
                    <i
                      className={`fa-solid ${value === "class" ? "fa-users" : "fa-user"}`}
                      aria-hidden="true"
                    />
                    {value === "class" ? tr("create.aClass") : tr("create.aStudent")}
                  </button>
                ))}
              </div>
            </div>
            <label>
              <span>
                {createForm.target === "class" ? tr("create.pickClass") : tr("create.pickStudent")}
              </span>
              <select
                value={createForm.targetId}
                onChange={(e) => setCreateForm((p) => ({ ...p, targetId: e.target.value }))}
              >
                <option value="">
                  {createForm.target === "class"
                    ? classes.length
                      ? tr("create.pickClass")
                      : tr("create.noClasses")
                    : allStudents.length
                      ? tr("create.pickStudent")
                      : tr("create.noStudents")}
                </option>
                {(createForm.target === "class" ? classes : allStudents).map((item) => (
                  <option key={item.id} value={String(item.id)}>
                    {item.name || item.full_name || `#${item.id}`}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>{tr("create.due")}</span>
              <input
                type="datetime-local"
                value={createForm.due_at}
                onChange={(e) => setCreateForm((p) => ({ ...p, due_at: e.target.value }))}
              />
            </label>
            <p className="hw-create__hint hw-create__full">{tr("create.hint")}</p>
            <div className="hw-create__actions hw-create__full">
              <button
                type="button"
                className="btn ghost"
                onClick={() => {
                  setCreating(false);
                  setCreateForm(EMPTY_CREATE);
                }}
              >
                {tr("create.cancel")}
              </button>
              <button type="submit" className="btn primary" disabled={createBusy}>
                {tr("create.submit")}
              </button>
            </div>
          </form>
        ) : null}

        {loading ? (
          <div className="homeworks-page__spinner" aria-hidden="true" />
        ) : isTeacherOrAdmin ? (
          <>
            {homeworks.length ? (
              <div className="hw-filters" role="tablist">
                {["all", "toReview", "draft", "published"].map((key) => (
                  <button
                    key={key}
                    type="button"
                    role="tab"
                    aria-selected={filter === key}
                    className={filter === key ? "is-active" : ""}
                    onClick={() => setFilter(key)}
                  >
                    {tr(`filters.${key}`)}
                    {key === "toReview" && toReviewTotal ? (
                      <span className="hw-filters__count">{toReviewTotal}</span>
                    ) : null}
                  </button>
                ))}
              </div>
            ) : null}
            {teacherList.length ? (
              <div className="hw-grid">{teacherList.map(renderTeacherCard)}</div>
            ) : (
              <p className="homeworks-page__empty">
                {homeworks.length ? tr("emptyFilter") : tr("empty")}
              </p>
            )}
          </>
        ) : homeworks.length ? (
          ["todo", "sent", "corrected"].map((key) =>
            studentGroups[key].length ? (
              <section className="hw-section" key={key}>
                <h2>
                  {tr(`student.sections.${key}`)}
                  <span className="hw-section__count">{studentGroups[key].length}</span>
                </h2>
                <ul className="hw-items">{studentGroups[key].map(renderStudentItem)}</ul>
              </section>
            ) : null,
          )
        ) : (
          <p className="homeworks-page__empty">{tr("student.empty")}</p>
        )}
      </div>
    </main>
  );

  return isStudent ? <StudentShell>{content}</StudentShell> : content;
}
